import React, { useState } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import { cancelPayment } from '../../api/payments';
import './CancelPaymentModal.css';

const CancelPaymentModal = ({ 
  isOpen, 
  onClose, 
  payment,
  onPaymentCancelled 
}) => {
  const [formData, setFormData] = useState({
    cancellation_date: new Date().toISOString().split('T')[0],
    cancellation_reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.cancellation_date || formData.cancellation_date.trim() === '') {
      newErrors.cancellation_date = 'Cancellation date is required';
    }
    
    if (!formData.cancellation_reason || formData.cancellation_reason.trim() === '') {
      newErrors.cancellation_reason = 'Cancellation reason is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await cancelPayment(payment.payment_id, formData);
      
      // Call the callback to refresh payment data
      if (onPaymentCancelled) {
        onPaymentCancelled(response.payment);
      }
      
      // Close modal
      onClose();
      
      // Reset form
      setFormData({
        cancellation_date: new Date().toISOString().split('T')[0],
        cancellation_reason: ''
      });
      
    } catch (error) {
      console.error('Error cancelling payment:', error);
      
      let errorMessage = 'Failed to cancel payment. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle different error response formats
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMessage = errorData.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Validation error'
          ).join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ 
        submit: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setErrors({});
      // Reset form
      setFormData({
        cancellation_date: new Date().toISOString().split('T')[0],
        cancellation_reason: ''
      });
    }
  };

  const footer = (
    <div className="cancel-payment-modal-actions">
      <RTButton 
        type="secondary" 
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </RTButton>
      <RTButton 
        type="primary" 
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Cancelling...' : 'Cancel Payment'}
      </RTButton>
    </div>
  );

  return (
    <RTModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cancel Payment"
      footer={footer}
      size="medium"
    >
      <div className="cancel-payment-modal-content">
        {payment && (
          <div className="payment-info">
            <h4>Payment Details</h4>
            <div className="payment-info-row">
              <span>Payment ID:</span>
              <span>#{payment.payment_id}</span>
            </div>
            <div className="payment-info-row">
              <span>Amount:</span>
              <span>₹{payment.amount}</span>
            </div>
            <div className="payment-info-row">
              <span>Payment Date:</span>
              <span>{new Date(payment.payment_date).toLocaleDateString()}</span>
            </div>
            <div className="payment-info-row">
              <span>Payment Method:</span>
              <span>{payment.payment_method}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="cancel-payment-form">
          <div className="form-group">
            <label htmlFor="cancellation_date">Cancellation Date</label>
            <input
              type="date"
              id="cancellation_date"
              name="cancellation_date"
              value={formData.cancellation_date}
              onChange={handleChange}
              className={errors.cancellation_date ? 'error' : ''}
              max={new Date().toISOString().split('T')[0]}
            />
            {errors.cancellation_date && (
              <span className="error-message">{errors.cancellation_date}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cancellation_reason">Cancellation Reason</label>
            <textarea
              id="cancellation_reason"
              name="cancellation_reason"
              value={formData.cancellation_reason}
              onChange={handleChange}
              className={errors.cancellation_reason ? 'error' : ''}
              placeholder="Please provide a reason for cancelling this payment..."
              rows="4"
            />
            {errors.cancellation_reason && (
              <span className="error-message">{errors.cancellation_reason}</span>
            )}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}
        </form>
      </div>
    </RTModal>
  );
};

export default CancelPaymentModal;
